import { Context, Effect, Layer, Ref, Schema, Scope } from "effect";
import { AtomRegistry, type Atom, type AsyncResult } from "effect/unstable/reactivity";
import { QueryWork } from "../state/query-work.ts";
import { makeSessionOwner } from "../state/session-owner.ts";
import type { OwnedRequest } from "../state/owned-requests.ts";
import { makeRecordingOwner } from "./owner.ts";
import { RecordingReplayIo } from "./replay-service.ts";
export const RecordingClockInput = Schema.Struct({ key: Schema.String, active: Schema.Boolean });
export type RecordingClockInput = typeof RecordingClockInput.Type;
export interface RecordingClockView {
    readonly error: string | null;
    readonly closed: boolean;
}
export interface RecordingClockSession {
    readonly snapshot: Atom.Atom<AsyncResult.AsyncResult<RecordingClockView>>;
    readonly read: Effect.Effect<RecordingClockView>;
    readonly receive: (input: RecordingClockInput, onDelta: (deltaMs: number) => Effect.Effect<void>) => Effect.Effect<void>;
}
export class RecordingClockSessions extends Context.Service<RecordingClockSessions, {
    readonly open: Effect.Effect<RecordingClockSession, never, Scope.Scope>;
}>()("workerstation/frontend/RecordingClockSessions") {
}
export const makeRecordingClock = Effect.fn("Frontend.Recording.clock")(function* (root: Scope.Scope, ports: {
    readonly io: typeof RecordingReplayIo.Service;
    readonly registry: AtomRegistry.AtomRegistry;
    readonly queryWork: typeof QueryWork.Service;
}) {
    interface State extends RecordingClockView {
        readonly input: RecordingClockInput;
        readonly sequence: number;
        readonly request: OwnedRequest | null;
        readonly onDelta: (deltaMs: number) => Effect.Effect<void>;
    }
    const initial: State = { input: { key: "", active: false }, sequence: 0, request: null, onDelta: () => Effect.void, error: null, closed: false };
    const life = yield* makeRecordingOwner(root, initial, ports, ({ error, closed }): RecordingClockView => ({ error, closed }));
    const receive = Effect.fn("Frontend.Recording.clock.receive")((input: RecordingClockInput, onDelta: State["onDelta"]) => Effect.gen(function* () {
        if (!life.alive())
            return;
        const before = yield* Ref.getAndUpdate(life.state, current => ({
            ...current, input, onDelta,
            sequence: current.sequence + (current.input.key !== input.key || current.input.active !== input.active ? 1 : 0),
            request: current.input.key !== input.key || current.input.active !== input.active ? null : current.request,
            error: current.input.key !== input.key || current.input.active !== input.active ? null : current.error
        }));
        if (before.input.key === input.key && before.input.active === input.active)
            return;
        const sequence = before.sequence + 1;
        if (before.request)
            yield* before.request.close;
        if (!life.alive() || !input.active || (yield* life.current).sequence !== sequence)
            return;
        yield* life.publish;
        const loop = Effect.gen(function* () {
            const started = yield* life.respond(ports.io.now);
            if (started._tag === "Failure")
                return started;
            let last = started.value;
            for (;;) {
                const tick = yield* life.respond(ports.io.frame);
                if (tick._tag === "Failure")
                    return tick;
                const current = yield* life.current;
                if (!life.alive() || current.sequence !== sequence || !current.input.active)
                    return { _tag: "Success" } as const;
                const delta = tick.value - last;
                last = tick.value;
                const delivered = yield* life.respond(Effect.suspend(() => current.onDelta(delta)));
                if (delivered._tag === "Failure")
                    return delivered;
            }
        });
        yield* life.queries.start(loop, (reply, request) => life.update(current => current.request?.identity !== request.identity ? current
            : { ...current, request: null, error: reply._tag === "Failure" ? reply.message : null }), request => Ref.modify(life.state, current => life.alive() && current.sequence === sequence && current.input.active
            ? [true, { ...current, request }] as const : [false, current] as const));
    }));
    return { snapshot: life.snapshot, read: life.read, receive } satisfies RecordingClockSession;
});
export const RecordingClockSessionsLive = (root: Scope.Scope) => Layer.effect(RecordingClockSessions, Effect.gen(function* () {
    const owner = yield* makeSessionOwner(root);
    const ports = { io: yield* RecordingReplayIo, registry: yield* AtomRegistry.AtomRegistry, queryWork: yield* QueryWork };
    return RecordingClockSessions.of({ open: owner.open(makeRecordingClock(root, ports)) });
}));
